import { addNotification } from "layouts/AppLayout/modules";
import { useDispatch } from "react-redux";
import { Action, Dispatch } from "redux";
import { CombinedError, OperationContext, OperationResult, UseMutationResponse } from "urql";
import { reportStudioError } from "utils/clientErrorReporting";

/**
 * Cleans graphql errors for end user's
 *
 * @param errorMessage - Error message
 */
export function cleanErrorMessage(errorMessage: string): string {
    return errorMessage
        .replace(/\[GraphQL\] /g, "")
        .replace(/\[Network\] /g, "");
}

export interface WrapUrqlFuncProps<Data> {
    onSuccessMessage?: boolean | string | ((data: Data) => { send: boolean, message?: string });
    onErrorMessage?: boolean | string | ((errors: string[]) => { send: boolean, message?: string });
}

export type MutationFunc<Data, Variables> = (variables?: Variables, context?: Partial<OperationContext>) => Promise<OperationResult<Data, Variables>>;

export function wrapUrqlMutation<Data = unknown, Variables = unknown>(mutation: UseMutationResponse<Data, Variables>, props: WrapUrqlFuncProps<Data> = {}): MutationFunc<Data, Variables> {
    const dispatch = useDispatch();
    const func = mutation[1];
    return (...args) => {
        return Promise.resolve() // Wrap the function call in a Promise chain.
            .then(() => func(...args))
            .catch((e) => {
                // Something when horribly wrong here.
                reportStudioError(e, "error");
                return {
                    error: e,
                    operation: {
                        context: undefined,
                        kind: undefined,
                    },
                } as OperationResult<Data, Variables>;
            })
            .then(async response => {

                const onSuccessMessage = props?.onSuccessMessage;

                if (response.error) {
                    await handleError(response.error, props, dispatch);
                } else if (typeof onSuccessMessage === "boolean" && onSuccessMessage !== false) {
                    handleSuccess(response, props, dispatch);
                } else if (typeof onSuccessMessage === "function") {
                    const success = onSuccessMessage(response.data);
                    if (success.send && success.message) {
                        props.onSuccessMessage = success.message;
                        handleSuccess(response, props, dispatch);
                    }
                } else if (typeof onSuccessMessage === "string") {
                    handleSuccess(response, props, dispatch)
                }
                return response;
            });
    };
}

async function handleError<Data = unknown>(error: CombinedError, props: WrapUrqlFuncProps<Data>, dispatch: Dispatch) {

    const { onErrorMessage } = props;
    const errors: string[] = [];
    let sendAsNotification = true;

    // A false
    if (onErrorMessage !== false) {
        // We are going to try to get an error message in a couple of ways
        // 1. In the onErrorMessage
        //     - If it is a string then use the error message
        const message: string = typeof onErrorMessage === "string" ? onErrorMessage : cleanErrorMessage(error.message);
        // If the message has any length to it add it.
        if (message.trim().length > 0) {
            errors.push(message);
            // dispatchOnError.unshift(addNotification(message, true));
        }

        // 2. Other thing to try is the actual response
        if (error.response) {
            const response = error.response;
            // You can only call response.json() once
            // otherwise it throws an error
            try {
                // Wrapped in a try catch because response.json() can only be called once
                // and it may have been called already
                const json = await response.json();
                if (Array.isArray(json.errors) && json.errors.length > 0) {
                    json.errors.forEach((bodyError: any) => {
                        errors.push(bodyError.message);
                        //  dispatchOnError.unshift(addNotification(bodyError.message, true));
                    });
                }
            } catch (e: any) {
                // Error occurred
                // Swallow the ones about stream already ready, they become a red herring
                // if an error happened in the execution
                if (!e.message.startsWith("Failed to execute 'json'")) {
                    console.error(e);
                }
            }
        }

        // see if it is a function and see if they want us to report it through notification
        if (typeof onErrorMessage === "function") {
            const callbackResult = onErrorMessage(errors);

            sendAsNotification = callbackResult.send;
            if (!!callbackResult.message) {

            }
        }
    }

    if (sendAsNotification) {
        for (const message of errors) {
            dispatch(addNotification(message, true));
        }
    }
}

function handleSuccess<Data = unknown>(response: OperationResult<unknown>, props: WrapUrqlFuncProps<Data>, dispatch: Dispatch) {
    const { onSuccessMessage } = props;
    const dispatchOnSuccess: Action[] = [];
    if (dispatch && onSuccessMessage !== false) {
        const message: string = typeof onSuccessMessage === "string" ?
            onSuccessMessage :
            "Success";
        dispatchOnSuccess.unshift(addNotification(message, false));
    }
    for (const itemToDispatch of dispatchOnSuccess) {
        dispatch(itemToDispatch);
    }
}